import { OrgRole } from '@/shared/types/userRoleTypes';

/**
 * Feature permissions configuration
 */
export enum Feature {
  SUBSCRIPTIONS = 'subscriptions',
  ADDRESSES = 'addresses',
  L2VPNS = 'l2vpns',
  INVOICES = 'invoices',
  USERS = 'users',
  CUSTOMERS = 'customers',
  SERVICE_ACCOUNTS = 'service_accounts',
  SERVICE_LINE = 'service_lines',
  SERVICE_LINE_SUBSCRIPTIONS = 'service_line_subscriptions',
  SERVICE_LINE_DEACTIVATION = 'service_line_deactivation',
  SERVICE_LINE_TERMINALS = 'service_line_terminals',
  TERMINALS = 'terminals',
  ROUTER_CONFIGS = 'router_configs',
}

export enum Permission {
  ALL = 'all',
  READ = 'read',
  MANAGE = 'manage',
}

type FeaturePermissions = Record<Feature, Permission[]>;

const PLATFORM_ADMIN_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [Permission.ALL],
  [Feature.ADDRESSES]: [Permission.ALL],
  [Feature.L2VPNS]: [Permission.ALL],
  [Feature.INVOICES]: [Permission.ALL],
  [Feature.USERS]: [Permission.ALL],
  [Feature.CUSTOMERS]: [Permission.ALL],
  [Feature.SERVICE_ACCOUNTS]: [Permission.ALL],
  [Feature.SERVICE_LINE]: [Permission.ALL],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.ALL],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.ALL],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.ALL],
  [Feature.TERMINALS]: [Permission.ALL],
  [Feature.ROUTER_CONFIGS]: [Permission.ALL],
};

const PLATFORM_OPERATOR_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [Permission.ALL],
  [Feature.ADDRESSES]: [Permission.ALL],
  [Feature.L2VPNS]: [Permission.ALL],
  [Feature.INVOICES]: [Permission.READ],
  [Feature.USERS]: [Permission.ALL],
  [Feature.CUSTOMERS]: [Permission.ALL],
  [Feature.SERVICE_ACCOUNTS]: [Permission.ALL],
  [Feature.SERVICE_LINE]: [Permission.ALL],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.ALL],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.ALL],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.ALL],
  [Feature.TERMINALS]: [Permission.ALL],
  [Feature.ROUTER_CONFIGS]: [Permission.ALL],
};

const PLATFORM_VIEWER_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [Permission.READ],
  [Feature.ADDRESSES]: [Permission.READ],
  [Feature.L2VPNS]: [Permission.READ],
  [Feature.INVOICES]: [Permission.READ],
  [Feature.USERS]: [Permission.READ],
  [Feature.CUSTOMERS]: [Permission.READ],
  [Feature.SERVICE_ACCOUNTS]: [Permission.READ],
  [Feature.SERVICE_LINE]: [Permission.READ],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.READ],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.READ],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.READ],
  [Feature.TERMINALS]: [Permission.READ],
  [Feature.ROUTER_CONFIGS]: [Permission.READ],
};

const TENANT_ADMIN_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [],
  [Feature.ADDRESSES]: [],
  [Feature.L2VPNS]: [],
  [Feature.INVOICES]: [],
  [Feature.USERS]: [Permission.ALL],
  [Feature.CUSTOMERS]: [Permission.ALL],
  [Feature.SERVICE_ACCOUNTS]: [],
  [Feature.SERVICE_LINE]: [Permission.ALL],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.ALL],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.ALL],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.ALL],
  [Feature.TERMINALS]: [Permission.ALL],
  [Feature.ROUTER_CONFIGS]: [Permission.ALL],
};

const TENANT_OPERATOR_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [],
  [Feature.ADDRESSES]: [],
  [Feature.L2VPNS]: [],
  [Feature.INVOICES]: [],
  [Feature.USERS]: [Permission.READ],
  [Feature.CUSTOMERS]: [Permission.ALL],
  [Feature.SERVICE_ACCOUNTS]: [],
  [Feature.SERVICE_LINE]: [Permission.ALL],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.ALL],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.ALL],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.ALL],
  [Feature.TERMINALS]: [Permission.ALL],
  [Feature.ROUTER_CONFIGS]: [Permission.ALL],
};

const TENANT_VIEW_ONLY_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [],
  [Feature.ADDRESSES]: [],
  [Feature.L2VPNS]: [],
  [Feature.INVOICES]: [],
  [Feature.USERS]: [Permission.READ],
  [Feature.CUSTOMERS]: [Permission.READ],
  [Feature.SERVICE_ACCOUNTS]: [],
  [Feature.SERVICE_LINE]: [Permission.READ],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.READ],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.READ],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.READ],
  [Feature.TERMINALS]: [Permission.READ],
  [Feature.ROUTER_CONFIGS]: [Permission.READ],
};

const CUSTOMER_ADMIN_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [],
  [Feature.ADDRESSES]: [],
  [Feature.L2VPNS]: [],
  [Feature.INVOICES]: [],
  [Feature.USERS]: [Permission.ALL],
  [Feature.CUSTOMERS]: [],
  [Feature.SERVICE_ACCOUNTS]: [],
  [Feature.SERVICE_LINE]: [Permission.ALL],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.READ],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.READ],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.READ],
  [Feature.TERMINALS]: [Permission.ALL],
  [Feature.ROUTER_CONFIGS]: [Permission.ALL],
};

const CUSTOMER_OPERATOR_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [],
  [Feature.ADDRESSES]: [],
  [Feature.L2VPNS]: [],
  [Feature.INVOICES]: [],
  [Feature.USERS]: [],
  [Feature.CUSTOMERS]: [],
  [Feature.SERVICE_ACCOUNTS]: [],
  [Feature.SERVICE_LINE]: [Permission.ALL],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.READ],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.READ],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.READ],
  [Feature.TERMINALS]: [Permission.ALL],
  [Feature.ROUTER_CONFIGS]: [Permission.ALL],
};

const CUSTOMER_VIEW_ONLY_ACCESS: FeaturePermissions = {
  [Feature.SUBSCRIPTIONS]: [],
  [Feature.ADDRESSES]: [],
  [Feature.L2VPNS]: [],
  [Feature.INVOICES]: [],
  [Feature.USERS]: [],
  [Feature.CUSTOMERS]: [],
  [Feature.SERVICE_ACCOUNTS]: [],
  [Feature.SERVICE_LINE]: [Permission.READ],
  [Feature.SERVICE_LINE_SUBSCRIPTIONS]: [Permission.READ],
  [Feature.SERVICE_LINE_DEACTIVATION]: [Permission.READ],
  [Feature.SERVICE_LINE_TERMINALS]: [Permission.READ],
  [Feature.TERMINALS]: [Permission.READ],
  [Feature.ROUTER_CONFIGS]: [Permission.READ],
};

export const ORG_ROLE_PERMISSIONS: Record<OrgRole, FeaturePermissions> = {
  [OrgRole.PLATFORM_ADMIN]: PLATFORM_ADMIN_ACCESS,
  [OrgRole.PLATFORM_OPERATOR]: PLATFORM_OPERATOR_ACCESS,
  [OrgRole.PLATFORM_VIEWER]: PLATFORM_VIEWER_ACCESS,
  [OrgRole.TENANT_ADMIN]: TENANT_ADMIN_ACCESS,
  [OrgRole.TENANT_OPERATOR]: TENANT_OPERATOR_ACCESS,
  [OrgRole.TENANT_VIEWER]: TENANT_VIEW_ONLY_ACCESS,
  [OrgRole.CUSTOMER_ADMIN]: CUSTOMER_VIEW_ONLY_ACCESS,
  [OrgRole.CUSTOMER_OPERATOR]: CUSTOMER_VIEW_ONLY_ACCESS,
  [OrgRole.CUSTOMER_VIEWER]: CUSTOMER_VIEW_ONLY_ACCESS,
};